import { useState } from "react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { Check, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PhoneInput } from "@/components/shared/PhoneInput";
import { EMPTY_PHONE, phoneToE164, type PhoneValue } from "@/utils/phone";
import { supabase } from "@/integrations/supabase/client";
import { fadeUp, viewportOnce } from "./motion-presets";

// El otro extremo del QR del flyer.
//
// El canal llevaba 73 escaneos, 6 altas y ninguna forma de contactar a los 67
// restantes: la telemetría del escaneo es un hash de IP a propósito
// (techalphamatrix/projects#371).
//
// SE AÑADE, NO SE INTERPONE. ReferralLanding documenta —y explica— la decisión
// de que el escaneo caiga en la landing que vende y no en un formulario: la
// persona es un plomero frío que acaba de escanear un QR en una barbería, y
// pedirle datos antes de contarle qué es esto se come las altas que sí hay.
// Así que esto vive DESPUÉS del precio, es una sección más de la página, y no
// bloquea ni desvía el camino al alta. Quien está listo para registrarse ya
// pasó por el botón de arriba; esto es para el que no lo está.
//
// SÓLO CUANDO HAY CÓDIGO. Sin referido no hay a quién atribuir ni canal que
// medir, y la landing general ya tiene su CTA.

type State = "idle" | "sending" | "sent";

export const PartnerLeadCapture = ({ code }: { code: string }) => {
  const { t, i18n } = useTranslation("referral");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState<PhoneValue>(EMPTY_PHONE);
  const [trade, setTrade] = useState("");
  const [state, setState] = useState<State>("idle");
  const [error, setError] = useState<string | null>(null);

  const e164 = phoneToE164(phone);
  const canSend = name.trim().length > 1 && e164 !== null && state === "idle";

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSend) return;
    setState("sending");
    setError(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke("partner-capture-lead", {
        // El idioma en el que esta persona está LEYENDO la página, no el del
        // navegador. Quien devuelve la llamada necesita saber en qué idioma
        // hablarle, y la columna venía siempre nula porque nadie la mandaba.
        body: {
          code,
          name: name.trim(),
          phone: e164,
          trade: trade.trim() || undefined,
          locale: i18n.language,
        },
      });
      // `deduped` también es un envío bueno: el segundo toque del mismo dedo no
      // es un fallo y decirle que lo es le hace mandarlo una tercera vez.
      if (fnError || !data?.ok) throw new Error("capture_failed");
      setState("sent");
    } catch {
      setState("idle");
      setError(t("lead.error"));
    }
  };

  if (state === "sent") {
    return (
      <section className="relative px-4 py-20">
        <div className="mx-auto max-w-xl rounded-xl border border-primary/30 bg-primary/5 p-8 text-center">
          <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/15">
            <Check className="h-6 w-6 text-primary" />
          </div>
          <h2 className="mt-5 font-display text-2xl font-semibold text-foreground">{t("lead.sentTitle")}</h2>
          <p className="mt-2 text-sm text-muted-foreground">{t("lead.sentBody")}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="relative px-4 py-20">
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        className="mx-auto max-w-xl rounded-xl border border-border bg-card/40 p-8 backdrop-blur"
      >
        <div className="text-center">
          <div className="mx-auto inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10">
            <Phone className="h-5 w-5 text-primary" />
          </div>
          <h2 className="mt-4 font-display text-2xl font-semibold tracking-tight text-foreground">
            {t("lead.title")}
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t("lead.subtitle")}</p>
        </div>

        <form onSubmit={submit} className="mt-7 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="lead-name">{t("lead.name")}</Label>
            <Input
              id="lead-name"
              className="h-11"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="lead-phone">{t("lead.phone")}</Label>
            <PhoneInput id="lead-phone" value={phone} onChange={setPhone} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="lead-trade">{t("lead.trade")}</Label>
            <Input
              id="lead-trade"
              className="h-11"
              placeholder={t("lead.tradePlaceholder")}
              value={trade}
              onChange={(e) => setTrade(e.target.value)}
              maxLength={80}
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <Button type="submit" size="lg" className="btn-gradient w-full border-0 text-white" disabled={!canSend} loading={state === "sending"}>
            {t("lead.cta")}
          </Button>

          {/* Consentimiento explícito: esto existe para que alguien llame o
              mande un SMS, y eso hay que decirlo antes, no después. */}
          <p className="text-center text-xs leading-relaxed text-muted-foreground">{t("lead.consent")}</p>
        </form>
      </motion.div>
    </section>
  );
};
